import React, { useState } from 'react';
import { PreEntry, Section, Category, TransactionType } from '../types';
import { apiService } from '../services/api';
import { SectionCategorySelector } from './SectionCategorySelector';
import CreateTemplateModal from './CreateTemplateModal';
import './PreEntryItem.css';

interface PreEntryItemProps {
  entry: PreEntry;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onUpdate: (updatedEntry: PreEntry) => void;
  onDelete: (entryId: string) => void;
  showCheckbox: boolean;
  sections: Section[] | null;
  loadCategories: (sectionName: string) => Promise<Category[]>;
}

const PreEntryItem: React.FC<PreEntryItemProps> = ({
  entry,
  selected,
  onSelect,
  onUpdate,
  onDelete,
  showCheckbox,
  sections,
  loadCategories
}) => {
  const [tempSection, setTempSection] = useState(entry.section || '');
  const [tempCategory, setTempCategory] = useState(entry.category || '');
  const [tempSectionId, setTempSectionId] = useState(entry.selected_section_id || '');
  const [tempCategoryId, setTempCategoryId] = useState(entry.selected_category_id || '');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Determine transaction type based on amount
  const transactionType: TransactionType = entry.amount >= 0 ? 'INCOME' : 'EXPENSE';

  const handleSectionChange = (sectionName: string, sectionId?: string) => {
    setTempSection(sectionName);
    setTempSectionId(sectionId || '');
    setTempCategory(''); // Clear category when section changes
    setTempCategoryId(''); // Clear category ID when section changes
    setHasChanges(true);
  };

  const handleCategoryChange = (categoryName: string, categoryId?: string) => {
    setTempCategory(categoryName);
    setTempCategoryId(categoryId || '');
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!tempSection || !tempCategory) {
      alert('Please select both section and category');
      return;
    }

    try {
      setSaving(true);
      
      const updatedEntry = await apiService.updatePreEntry(entry.id, {
        section: tempSection,
        category: tempCategory,
        selected_section_id: tempSectionId,
        selected_section_name: tempSection,
        selected_category_id: tempCategoryId,
        selected_category_name: tempCategory,
      });

      onUpdate(updatedEntry);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving pre-entry:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTempSection(entry.section || '');
    setTempCategory(entry.category || '');
    setTempSectionId(entry.selected_section_id || '');
    setTempCategoryId(entry.selected_category_id || '');
    setHasChanges(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await apiService.deletePreEntry(entry.id);
        onDelete(entry.id);
      } catch (error) {
        console.error('Error deleting pre-entry:', error);
        alert('Failed to delete entry. Please try again.');
      }
    }
  };

  const handleCreateTemplate = () => {
    if (!tempSection || !tempCategory || !tempSectionId || !tempCategoryId) {
      alert('Please select both section and category before creating a template');
      return;
    }
    setShowTemplateModal(true);
  };

  const handleTemplateCreated = () => {
    // You might want to show a success message here
    console.log('Template created successfully');
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR', // Adjust based on your currency
    }).format(Math.abs(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusClass = () => {
    switch (entry.status) {
      case 'saved':
        return 'saved';
      case 'processed':
        return 'processed';
      case 'pending':
      default:
        return 'pending';
    }
  };

  return (
    <div className={`pre-entry-item ${getStatusClass()} ${selected ? 'selected' : ''}`}>
      {/* Checkbox */}
      {showCheckbox && (
        <div className="entry-checkbox">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
          />
        </div>
      )}

      {/* Entry Header */}
      <div className="entry-header">
        <div className="entry-date">{formatDate(entry.date)}</div>
        <div className={`amount ${entry.amount >= 0 ? 'positive' : 'negative'}`}>
          {entry.amount >= 0 ? '+' : ''}{formatAmount(entry.amount)}
        </div>
      </div>

      {/* Description */}
      <div className="entry-description">{entry.description}</div>

      {/* Transaction Type Badge */}
      <div className="metadata-row">
        <span className="label">Type:</span>
        <span className={`value ${transactionType === 'INCOME' ? 'positive' : 'negative'}`}>
          {transactionType}
        </span>
      </div>

      {/* Status */}
      <div className="metadata-row">
        <span className="label">Status:</span>
        <span className={`value status-${entry.status}`}>
          {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
        </span>
      </div>

      {/* Bank Type */}
      {entry.bank_type && (
        <div className="metadata-row">
          <span className="label">Bank:</span>
          <span className={`value bank-badge bank-${entry.bank_type.toLowerCase()}`}>
            {entry.bank_type.toUpperCase()}
          </span>
        </div>
      )}

      {/* Section & Category Selection */}
      <div className="entry-content">
        <SectionCategorySelector
          currentSection={tempSection}
          currentCategory={tempCategory}
          transactionType={transactionType}
          onSectionChange={handleSectionChange}
          onCategoryChange={handleCategoryChange}
          disabled={saving}
          sections={sections}
          loadCategories={loadCategories}
        />

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            onClick={handleSave}
            disabled={!hasChanges || !tempSection || !tempCategory || saving}
            className="save-button"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          
          {hasChanges && (
            <button
              onClick={handleReset}
              disabled={saving}
              className="cancel-button"
            >
              Reset
            </button>
          )}

          <button
            onClick={handleCreateTemplate}
            disabled={!tempSection || !tempCategory || !tempSectionId || !tempCategoryId || saving}
            className="template-button"
            title="Create template for future auto-categorization"
          >
            Create Template
          </button>
          
          <button
            onClick={handleDelete}
            disabled={saving}
            className="delete-button"
          >
            Delete
          </button>
        </div>

        {/* Status Indicator */}
        {tempSection && tempCategory && (
          <div className="entry-warning">
            Ready to process: <strong>{tempSection}</strong> → <strong>{tempCategory}</strong>
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      <CreateTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        initialPattern={entry.description}
        sectionId={tempSectionId}
        sectionName={tempSection}
        categoryId={tempCategoryId}
        categoryName={tempCategory}
        onTemplateCreated={handleTemplateCreated}
      />
    </div>
  );
};

export default PreEntryItem;